import { useInfiniteQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router'
import type { AccountList, AccountStatus, StoreEnvironment } from '../../../api/accounts'
import { api, query } from '../../../api/client'
import { useDebounced } from '../../../hooks/useDebounced'
import { formatDate, formatRelative } from '../../../lib/format'
import { NewAccountForm } from './NewAccountForm'
import { SignInIcons } from './SignInIcons'
import { EnvironmentBadge, StatusBadge } from './StatusBadge'

const STATUSES: AccountStatus[] = ['active', 'grace', 'granted', 'expired', 'revoked', 'suspended', 'none']

/** Browse and search accounts. The search box takes anything that identifies one (see the server's `searchAccounts`). */
export function AccountsPanel() {
	const [q, setQ] = useState('')
	const [status, setStatus] = useState<AccountStatus | ''>('')
	const [environment, setEnvironment] = useState<StoreEnvironment | ''>('')
	const search = useDebounced(q.trim(), 300)
	const accounts = useInfiniteQuery({
		queryKey: ['admin-accounts', search, status, environment],
		initialPageParam: undefined as string | undefined,
		queryFn: ({ pageParam }) =>
			api.get<AccountList>(`/api/v1/admin/accounts${query({ q: search, status, environment, cursor: pageParam })}`),
		getNextPageParam: (page) => page.nextCursor ?? undefined,
	})
	const rows = accounts.data?.pages.flatMap((p) => p.accounts) ?? []

	return (
		<>
			<NewAccountForm />
			<section className="card stack">
				<div className="row" style={{ flexWrap: 'wrap' }}>
					<input
						type="search"
						aria-label="Search accounts"
						placeholder="Support ID, Order ID, transaction id, account id, device or app name…"
						value={q}
						onChange={(e) => setQ(e.target.value)}
						style={{ flex: 1, minWidth: '16rem' }}
					/>
					<select aria-label="Access" value={status} onChange={(e) => setStatus(e.target.value as AccountStatus | '')}>
						<option value="">Any access</option>
						{STATUSES.map((s) => (
							<option key={s} value={s}>
								{s}
							</option>
						))}
					</select>
					<select
						aria-label="Environment"
						value={environment}
						onChange={(e) => setEnvironment(e.target.value as StoreEnvironment | '')}
					>
						<option value="">Any environment</option>
						<option value="Production">Production</option>
						<option value="Sandbox">Sandbox</option>
						<option value="Xcode">Xcode</option>
					</select>
				</div>
				{accounts.isError && <p className="error small">{accounts.error.message}</p>}
				<div className="table-scroll">
					<table>
						<thead>
							<tr>
								<th>Support ID</th>
								<th>Sign-in</th>
								<th>Access</th>
								<th>Until</th>
								<th className="num">Devices</th>
								<th>Last seen</th>
								<th>Created</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((a) => (
								<tr key={a.id}>
									<td>
										<Link to={`/admin/accounts/${a.id}`} className="mono">
											{a.supportId}
										</Link>
									</td>
									<td>
										<SignInIcons account={a} />
									</td>
									<td>
										<StatusBadge status={a.status} />
										<EnvironmentBadge environment={a.environment} />
									</td>
									<td className="muted small">{a.expiresAt ? formatDate(a.expiresAt) : '—'}</td>
									<td className="num">{a.devices}</td>
									<td className="muted small">{formatRelative(a.lastSeenAt)}</td>
									<td className="muted small">{formatDate(a.createdAt)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				{accounts.isSuccess && !rows.length && (
					<p className="muted">{search ? 'Nothing matches.' : 'No accounts yet.'}</p>
				)}
				{accounts.hasNextPage && (
					<button type="button" onClick={() => accounts.fetchNextPage()} disabled={accounts.isFetchingNextPage}>
						More
					</button>
				)}
			</section>
		</>
	)
}
